import React from 'react';
import { MessageCircle } from 'lucide-react';

// رقم واتساب المكتب
const OFFICE_WHATSAPP = '966568228974';

/**
 * زر تواصل واتساب
 * @param {string} phoneNumber - رقم جوال العميل (اختياري)
 * @param {string} clientName - اسم العميل (اختياري)
 * @param {string} context - سياق المحادثة (مثل: رقم القضية، الطلب)
 * @param {string} size - حجم الزر: 'sm', 'md', 'lg'
 * @param {boolean} showText - إظهار النص
 * @param {string} className - classes إضافية
 */
export function WhatsAppButton({ 
  phoneNumber, 
  clientName = '', 
  context = '',
  size = 'md',
  showText = true,
  className = ''
}) {
  const handleClick = () => {
    // تنظيف رقم الجوال (إزالة المسافات والرموز)
    let cleanNumber = phoneNumber?.replace(/[\s\-\(\)]/g, '') || '';
    
    // إذا لم يكن هناك رقم، نستخدم رقم المكتب
    if (!cleanNumber) {
      cleanNumber = OFFICE_WHATSAPP;
    }
    
    // إضافة رمز الدولة إذا لم يكن موجوداً
    if (cleanNumber.startsWith('05')) {
      cleanNumber = '966' + cleanNumber.substring(1);
    } else if (cleanNumber.startsWith('5')) {
      cleanNumber = '966' + cleanNumber;
    } else if (!cleanNumber.startsWith('966') && !cleanNumber.startsWith('+')) {
      cleanNumber = '966' + cleanNumber;
    }
    
    // إزالة + إذا وجدت
    cleanNumber = cleanNumber.replace('+', '');
    
    // إنشاء رسالة ترحيبية
    let message = `مرحباً`;
    if (clientName) {
      message += ` ${clientName}`;
    }
    message += `،\n\nمعك مكتب هشام يوسف الخياط للمحاماة.`;
    if (context) {
      message += `\n\nبخصوص: ${context}`;
    }
    message += `\n\nكيف يمكننا مساعدتك؟`;
    
    // فتح واتساب
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs gap-1',
    md: 'px-3 py-2 text-sm gap-2',
    lg: 'px-4 py-3 text-base gap-2',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center justify-center bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-body ${sizeClasses[size]} ${className}`}
      title="تواصل عبر واتساب"
      data-testid="whatsapp-btn"
    >
      <MessageCircle className={iconSizes[size]} />
      {showText && <span>واتساب</span>}
    </button>
  );
}

/**
 * أيقونة واتساب عائمة
 */
export function WhatsAppFloatingIcon({ phoneNumber, clientName, context }) {
  const handleClick = () => {
    let cleanNumber = phoneNumber?.replace(/[\s\-\(\)]/g, '') || OFFICE_WHATSAPP;
    
    if (cleanNumber.startsWith('05')) {
      cleanNumber = '966' + cleanNumber.substring(1);
    } else if (cleanNumber.startsWith('5')) {
      cleanNumber = '966' + cleanNumber;
    } else if (!cleanNumber.startsWith('966') && !cleanNumber.startsWith('+')) {
      cleanNumber = '966' + cleanNumber;
    }
    cleanNumber = cleanNumber.replace('+', '');
    
    let message = `مرحباً`;
    if (clientName) message += ` ${clientName}`;
    message += `،\n\nمعك مكتب هشام يوسف الخياط للمحاماة.`;
    if (context) message += `\n\nبخصوص: ${context}`;
    message += `\n\nكيف يمكننا مساعدتك؟`;
    
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <button
      onClick={handleClick}
      className="w-12 h-12 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 hover:shadow-xl transition-all flex items-center justify-center"
      title="تواصل عبر واتساب"
      data-testid="whatsapp-floating-btn"
    >
      <MessageCircle className="w-6 h-6" />
    </button>
  );
}

/**
 * رابط واتساب نصي
 */
export function WhatsAppLink({ phoneNumber, clientName, context, children }) {
  const handleClick = (e) => {
    e.preventDefault();
    let cleanNumber = phoneNumber?.replace(/[\s\-\(\)]/g, '') || OFFICE_WHATSAPP;
    
    if (cleanNumber.startsWith('05')) {
      cleanNumber = '966' + cleanNumber.substring(1);
    } else if (cleanNumber.startsWith('5')) {
      cleanNumber = '966' + cleanNumber;
    } else if (!cleanNumber.startsWith('966') && !cleanNumber.startsWith('+')) {
      cleanNumber = '966' + cleanNumber;
    }
    cleanNumber = cleanNumber.replace('+', '');
    
    let message = `مرحباً`;
    if (clientName) message += ` ${clientName}`;
    message += `،\n\nمعك مكتب هشام يوسف الخياط للمحاماة.`;
    if (context) message += `\n\nبخصوص: ${context}`;
    message += `\n\nكيف يمكننا مساعدتك؟`;
    
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <a
      href="#"
      onClick={handleClick}
      className="text-green-600 hover:text-green-700 hover:underline inline-flex items-center gap-1"
    >
      <MessageCircle className="w-4 h-4" />
      {children || 'واتساب'}
    </a>
  );
}

export default WhatsAppButton;
